/**
 * Chart color palette — TIO brand colors with Peel-style depth.
 *
 * Each color has a main value plus gradient and glow variants for
 * canvas-based charts that use fills, shadows, and area gradients.
 */
export const DEFAULT_CHART_COLORS = [
  '#0e7490', // teal (ocean-600)
  '#6e44c1', // purple (violet-600)
  '#2dd4bf', // mint (brighter teal for contrast)
  '#998ce3', // lavender (violet-300)
  '#2563eb', // blue
  '#059669', // emerald
  '#f59e0b', // amber
  '#dc2626', // red
  '#77bcc0', // light teal
  '#7c2d12', // brown
];

/**
 * Extended color set with gradient pairs for area/bar fills.
 * Each entry: [main, lighter variant for gradients].
 */
export const GRADIENT_PAIRS: [string, string][] = [
  ['#0e7490', '#22d3ee'], // teal → cyan
  ['#6e44c1', '#a78bfa'], // violet → lavender
  ['#2dd4bf', '#5eead4'], // mint → light mint
  ['#998ce3', '#c4b5fd'], // lavender → soft lilac
  ['#2563eb', '#60a5fa'], // blue → light blue
  ['#059669', '#34d399'], // emerald → light green
  ['#f59e0b', '#fbbf24'], // amber → yellow
  ['#dc2626', '#f87171'], // red → light red
  ['#77bcc0', '#a5f3fc'], // light teal → ice
  ['#7c2d12', '#ea580c'], // brown → orange
];

/**
 * Detect if dark mode is currently active via the document root element.
 */
export function isDarkMode(): boolean {
  if (typeof document === 'undefined') return false;
  return document.documentElement.classList.contains('dark');
}

/**
 * Theme-aware color configuration for chart axes, grid lines, and text.
 * Dark mode uses the Peel-style muted tones; light mode stays crisp.
 */
export function getThemeColors() {
  const dark = isDarkMode();
  return {
    text: dark ? '#ececf0' : '#1f2937',
    textMuted: dark ? '#6b6b7b' : '#6b7280',
    textSubtle: dark ? '#45455a' : '#9ca3af',
    grid: dark ? 'rgba(255,255,255,0.06)' : '#e5e7eb',
    axis: dark ? '#6b7280' : '#9ca3af',
    background: 'transparent',
    tooltipBg: dark ? 'rgba(15,16,18,0.92)' : '#ffffff',
    tooltipText: dark ? '#ececf0' : '#1f2937',
    tooltipBorder: dark ? 'rgba(255,255,255,0.08)' : '#e5e7eb',
    // Peel-style glow accents
    glowTeal: dark ? 'rgba(45,212,191,0.15)' : 'rgba(14,116,144,0.08)',
    glowViolet: dark ? 'rgba(124,127,255,0.15)' : 'rgba(110,68,193,0.08)',
  };
}

/**
 * Convert hex color to rgba string.
 */
export function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}
